import { ContextService } from '../services/context.service';
import { MUser } from './data.model';

export class ArrayMap<TKey, TItem> {
  private keyProperty: string;
  private map = new Map<TKey, number>();

  get array(): TItem[] {
    return this._array;
  }
  set array(value: TItem[]) {
    this._array = value;
    this.map.clear();

    for (let i = 0; i < value.length; i++) {
      this.map.set(this.getKey(value[i]), i);
    }
  }
  private _array: TItem[] = [];

  get size(): number {
    return this._array.length;
  }

  constructor(keyProperty: string) {
    this.keyProperty = keyProperty;
  }

  set(item: TItem): void {
    this.map.set(this.getKey(item), this._array.length);
    this._array.push(item);
  }

  get(key: TKey): TItem | undefined {
    const index = this.getIndex(key);
    return index >= 0 ? this._array[index] : undefined;
  }

  has(key: TKey): boolean {
    return this.map.has(key);
  }

  delete(key: TKey): boolean {
    const index = this.getIndex(key);

    if (index >= 0) {
      this.map.delete(key);
      this._array.splice(index, 1);

      for (let i = index; i < this._array.length; i++) {
        this.map.set(this.getKey(this._array[i]), i);
      }

      return true;
    }

    return false;
  }

  clear(soft = false): void {
    this.map.clear();

    if (!soft) {
      this._array.length = 0;
    }

    this._array = [];
  }

  private getIndex(key: TKey): number {
    return this.map.get(key) ?? -1;
  }

  private getKey(item: TItem): TKey {
    return (<any>item)[this.keyProperty];
  }
}

export abstract class ViewModelBase {
  private originalValues?: Map<string, any>;

  get id(): string {
    return '';
  }

  get isValid(): boolean {
    return false;
  }

  get displayText(): string {
    return this.displayTextCore ?? '';
  }
  protected get displayTextCore(): string | undefined {
    return this.item?.DisplayText;
  }

  get isNew(): boolean {
    return !this.id;
  }

  get hasChanges(): boolean {
    return (this.originalValues?.size ?? 0) > 0;
  }

  get canSave(): boolean {
    return (this.isNew || this.hasChanges) && this.isValid;
  }

  constructor(protected contextService: ContextService, private item?: any) {}

  protected super(item?: any) {
    if (item) {
      this.item = item;
    }
  }

  prepareBeforeRouting(): ViewModelBase {
    const _this = <any>this;
    _this.contextService = undefined;

    return this;
  }

  prepareAfterRouting(contextService: ContextService) {
    this.contextService = contextService;
  }

  protected isChanged(property: string): boolean {
    return this.originalValues !== undefined && this.originalValues.has(property);
  }

  protected getOriginalValue(property: string): any {
    return this.isChanged(property) ? this.originalValues!.get(property) : this.getValueCore(this.item, property);
  }

  protected setValue(key: string, oldValue: any, newValue: any): boolean {
    if (oldValue === newValue) {
      return false;
    }

    this.setValueCore(this.item, key, newValue);

    if (!this.originalValues) {
      this.originalValues = new Map<string, any>();
    }

    if (!this.originalValues.has(key)) {
      this.originalValues.set(key, oldValue);
    } else if (this.originalValues.get(key) === newValue) {
      this.originalValues.delete(key);
    }

    return true;
  }

  private setValueCore(item: any, key: string, value: any): void {
    if (key.indexOf('.') >= 0) {
      const keys = key.split('.');

      for (let i = 0; i < keys.length - 1; i++) {
        item = item[keys[i]];
      }

      item[keys[keys.length - 1]] = value;
    } else {
      item[key] = value;
    }
  }

  private getValueCore(item: any, key: string): any {
    if (key.indexOf('.') >= 0) {
      const keys = key.split('.');

      for (let i = 0; i < keys.length - 1; i++) {
        item = item[keys[i]];
      }

      return item[keys[keys.length - 1]];
    } else {
      return item[key];
    }
  }

  reset(): void {
    if (this.originalValues) {
      this.originalValues.forEach((value, key) => {
        this.setValueCore(this.item, key, value);
      });
      this.originalValues = undefined;
    }
  }
}

export class PasswordViewModel extends ViewModelBase {
  get currentPassword(): string {
    return this.data.currentPassword ?? '';
  }
  set currentPassword(value: string) {
    this.setValue('currentPassword', this.currentPassword, value ?? '');
  }

  get newPassword(): string {
    return this.data.newPassword ?? '';
  }
  set newPassword(value: string) {
    this.setValue('newPassword', this.newPassword, value ?? '');
  }

  get confirmPassword(): string {
    return this.data.confirmPassword ?? '';
  }
  set confirmPassword(value: string) {
    this.setValue('confirmPassword', this.confirmPassword, value ?? '');
  }

  override get isValid(): boolean {
    const currentPassword = this.currentPassword.trim();
    const newPassword = this.newPassword.trim();
    const confirmPassword = this.confirmPassword.trim();

    if (
      !currentPassword ||
      !newPassword ||
      !confirmPassword ||
      currentPassword.length !== this.currentPassword.length ||
      newPassword.length !== this.newPassword.length ||
      confirmPassword.length !== this.confirmPassword.length ||
      newPassword !== confirmPassword ||
      newPassword === currentPassword
    ) {
      return false;
    }

    return true;
  }

  private get data(): any {
    return (<any>this).item;
  }

  constructor(contextService: ContextService) {
    super(contextService, {});
  }
}

export class UserViewModel extends ViewModelBase {
  override get id(): string {
    return this.muser.Id ?? '';
  }

  get parentId(): string {
    return this.muser.ParentId ?? '';
  }
  set parentId(value: string) {
    this.setValue('ParentId', this.parentId, value ?? '');
  }

  get email(): string {
    return this.muser.EMail ?? '';
  }
  set email(value: string) {
    this.setValue('EMail', this.email, value ?? '');
  }

  get login(): string {
    return this.muser.Login ?? '';
  }
  set login(value: string) {
    this.setValue('Login', this.login, value ?? '');
  }

  get loginMode(): number | null {
    return this.muser.LoginMode ?? null;
  }
  set loginMode(value: number | null) {
    this.setValue('LoginMode', this.loginMode, value ?? null);
  }

  get maxSessionsCount(): number | null {
    return this.muser.MaxSessionsCount ?? null;
  }
  set maxSessionsCount(value: number | null) {
    this.setValue('MaxSessionsCount', this.maxSessionsCount, value ?? null);
  }

  get minSessionsCount(): number | null {
    return this.muser.MinSessionsCount ?? null;
  }
  set minSessionsCount(value: number | null) {
    this.setValue('MinSessionsCount', this.minSessionsCount, value ?? null);
  }

  get name(): string {
    return this.muser.Name ?? '';
  }
  set name(value: string) {
    this.setValue('Name', this.name, value ?? '');
  }

  get passwordHash(): string {
    return this.muser.PasswordHash ?? '';
  }
  set passwordHash(value: string) {
    this.setValue('PasswordHash', this.passwordHash, value ?? '');
  }

  override get displayText(): string {
    return this.displayTextCore ? this.displayTextCore : `${this.name}`;
  }

  override get isValid(): boolean {
    const nameTrimmed = this.name?.trim();
    const name = nameTrimmed?.toUpperCase();

    if (!name || nameTrimmed.length !== this.name.length || !this.parentId) {
      return false;
    }

    return !this.contextService.users.array.some(
      (user) => user.id !== this.id && user.parentId === this.parentId && user.name.toUpperCase() === name
    );
  }

  get password(): PasswordViewModel {
    if (!this._password) {
      this._password = new PasswordViewModel(this.contextService);
    }

    return this._password;
  }
  private _password: PasswordViewModel;

  constructor(contextService: ContextService, readonly muser: MUser) {
    super(contextService, muser);
  }
}
